import {
  Typography,
  bodySmallTypography,
  boldBodyTypography,
  elevation1,
  mediumBodySmallTypography,
  neutral0,
  neutral40,
  seafoam60lime60Gradient,
  slugifyString,
  spacing1,
  spacing2,
  spacing3,
  spacing4,
} from '@leaftrade/leaftrade-component-library';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { useStorefrontState } from '../../../hooks/useReduxState';
import { getBoxShadowStringFromElevation } from '../../../utils';
import {
  GRID_ITEM_CSS_CLASS,
  QUICK_ORDER_PATH,
  STOREFRONT_MODE,
} from '../../constants';
import AddToCart from '../ProductCard/AddToCart';
import AddToQuickOrder from '../ProductCard/AddToQuickOrder';
import ProductBrands from '../ProductCard/ProductBrands';
import {
  ProductChipsWrapper,
  ProductInfoWrapper,
  ProductPriceWrapper,
  ProductQuantityWrapper,
  StyledProductCard,
  VariantInfoWrapper,
} from '../ProductCard/ProductCard.styles';
import ProductChip from '../ProductCard/ProductChip';
import ProductImage from '../ProductCard/ProductImage';
import ProductName from '../ProductCard/ProductName';
import ProductVariants from '../ProductCard/ProductVariants';
import VariantInfo from '../ProductCard/VariantInfo';

const ProductCardBatchQueues = ({ product, slug }) => {
  const { cart, hasLimitedAccess, sellerPageData, vendor } =
    useStorefrontState();
  const { selectedCart } = cart;
  const { productTypes } = sellerPageData;
  const { displayLiveInventoryCount, isGridView, settingsDeprecated } =
    vendor.settings;
  const { storefront_mode: storefrontMode } = settingsDeprecated;
  const { package_sizes: packageSizes } = product;

  const [alert, setAlert] = useState();
  const [variantInCart, setVariantInCart] = useState(false);
  const [selectedPackageSize, setSelectedPackageSize] = useState(
    packageSizes[0]
  );

  const isQuickOrderPage = window.location.pathname.includes(QUICK_ORDER_PATH);
  const selectedVariant = selectedPackageSize.variant;

  const {
    id: selectedPackageSizeId,
    inventory_live_count: inventoryLiveCount,
  } = selectedPackageSize;

  const {
    brands,
    category_display_name: categoryDisplayName,
    default_image: defaultImage,
    derived_phenotype: derivedPhenotype,
    price,
    price_display: priceDisplay,
    price_per_unit: pricePerUnit,
    product_id: productId,
    product_name: productName,
    size_unit: sizeUnit,
    tags,
  } = selectedVariant;

  const isAncillaryOnly =
    productTypes.length === 1 && productTypes[0].name === 'Ancillary';

  const hideAddToCart =
    storefrontMode === STOREFRONT_MODE.READ_ONLY_MENU || hasLimitedAccess;

  const renderTags = useCallback(
    () =>
      tags
        ? tags.map((tag) => <ProductChip key={tag} text={tag} type='tag' />)
        : null,
    [tags]
  );

  useEffect(() => {
    if (selectedCart.cartItems?.length > 0) {
      const cartItem = selectedCart.cartItems.find(
        (item) => item.packageSizeId === selectedVariant.package_size_id
      );
      if (cartItem) {
        setVariantInCart(cartItem);
      } else {
        setVariantInCart(false);
      }
    } else {
      setVariantInCart(false);
    }
  }, [selectedCart.cartItems, selectedVariant.package_size_id]);

  return (
    <StyledProductCard
      data-testid='product-card-batch-queues--component--div'
      $isGridView={isGridView}
      className={GRID_ITEM_CSS_CLASS}
    >
      {variantInCart && (
        <Typography
          bodyStyle={mediumBodySmallTypography}
          sx={{
            position: 'absolute',
            background: seafoam60lime60Gradient,
            left: 0,
            padding: `${spacing2} ${spacing4}`,
            color: neutral0.hex,
            borderRadius: '0 8px 8px 0px',
            top: spacing4,
            boxShadow: getBoxShadowStringFromElevation(elevation1()),
          }}
        >
          {variantInCart.totalQuantity} in cart
        </Typography>
      )}
      {isGridView && (
        <ProductInfoWrapper $isGridView>
          <ProductChipsWrapper
            style={{
              marginTop: spacing1,
              paddingLeft: variantInCart
                ? `${3.5 + 0.5 * String(variantInCart.totalQuantity).length}rem`
                : 0,
            }}
          >
            {renderTags()}
          </ProductChipsWrapper>
        </ProductInfoWrapper>
      )}
      <ProductImage
        imgSrc={defaultImage}
        name={productName}
        productId={productId}
        selectedVariantId={selectedPackageSizeId}
        slug={slug}
      />
      <ProductInfoWrapper $isGridView={isGridView}>
        <ProductChipsWrapper>
          {!isGridView && renderTags()}
          {categoryDisplayName && (
            <ProductChip text={categoryDisplayName} type='category' />
          )}
          {derivedPhenotype && (
            <ProductChip
              text={derivedPhenotype}
              type={slugifyString(derivedPhenotype)}
            />
          )}
        </ProductChipsWrapper>
        <ProductName
          name={productName}
          productId={productId}
          selectedVariantId={selectedPackageSizeId}
          slug={slug}
        />
        <ProductBrands brands={brands} />
        <ProductVariants
          selectedVariantId={selectedPackageSizeId}
          setAlert={setAlert}
          setSelectedVariant={setSelectedPackageSize}
          variants={packageSizes}
        />
      </ProductInfoWrapper>
      {!isAncillaryOnly && (
        <VariantInfoWrapper $isGridView={isGridView}>
          {selectedVariant?.batch?.id !== 0 && (
            <VariantInfo
              selectedVariant={selectedVariant}
              productId={product.id}
              slug={slug}
            />
          )}
        </VariantInfoWrapper>
      )}
      <ProductPriceWrapper $isGridView={isGridView}>
        <Typography
          bodyStyle={!isGridView ? boldBodyTypography : null}
          component='h4'
          testingId='product-card-batch-queues--price--typography-p'
        >
          {selectedVariant ? priceDisplay : price || ''}
        </Typography>
        <Typography
          gutterBottom
          sx={{
            color: neutral40.hex,
            marginTop: spacing3,
          }}
          testingId='product-card-batch-queues--size--typography-p'
        >
          {sizeUnit ? `${pricePerUnit}/${sizeUnit}` : ''}
        </Typography>
      </ProductPriceWrapper>
      <ProductQuantityWrapper $isGridView={isGridView}>
        {displayLiveInventoryCount && (
          <Typography
            align='right'
            bodyStyle={!isGridView ? bodySmallTypography : null}
            testingId='product-card-batch-queues--live-inventory--typography-p'
          >
            Current Inventory:{' '}
            {inventoryLiveCount >= 0 ? inventoryLiveCount : 0}
          </Typography>
        )}
        {!hideAddToCart && (
          <>
            {isQuickOrderPage ? (
              <AddToQuickOrder
                inventoryLiveCount={inventoryLiveCount}
                productId={productId}
                selectedVariant={selectedVariant}
              />
            ) : (
              <AddToCart
                alert={alert}
                productId={productId}
                selectedVariant={selectedVariant}
                setAlert={setAlert}
                slug={slug}
              />
            )}
          </>
        )}
      </ProductQuantityWrapper>
    </StyledProductCard>
  );
};

ProductCardBatchQueues.propTypes = {
  product: PropTypes.object.isRequired,
  slug: PropTypes.string.isRequired,
};

export default ProductCardBatchQueues;
